import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './TradeTree.css';

interface Player {
  player_id: string;
  first_name: string;
  last_name: string;
  position: string;
  team: string;
}

interface League {
  league_id: string;
  name: string;
  season: string;
  previous_league_id: string;
}

interface Transaction {
  type: string;
  transaction_id: string;
  status: string;
  roster_ids: number[];
  adds: Record<string, number>;
  drops: Record<string, number>;
  creator: string;
  consenter_ids: number[];
  created: number;
  metadata: {
    notes?: string;
  };
  leg?: number;
  waiver_budget?: number;
  trade_ids?: string[];
  draft_picks?: {
    season: string;
    round: number;
    roster_id: number;
    previous_owner_id: number;
    owner_id: number;
  }[];
}

// Trade Tree specific interfaces
interface TradeTreePlayer {
  id: string;
  name: string;
}

interface TradeTreePick {
  year: string;
  round: number;
  pickNumber?: number;
  playerDrafted?: TradeTreePlayer;
}

interface TradeTreeTrader {
  team: string;
  tradedAwayPicks: TradeTreePick[];
  tradedAwayPlayers: TradeTreePlayer[];
  receivedPicks: TradeTreePick[];
  receivedPlayers: TradeTreePlayer[];
}

interface TradeTreeTrade {
  id: string;
  date: string;
  traders: TradeTreeTrader[];
}

// Draft pick interfaces
interface DraftPick {
  pick_no: number;
  player_id: string;
  picked_by: string;
  roster_id: number;
  round: number;
  draft_slot: number;
  draft_id: string;
  season: string;
  league_id: string;
  player_name?: string;
  team_name?: string;
}

// Draft data for leagues (only includes drafts with 2-3 rounds)
interface DraftData {
  draft_id: string;
  season: string;
  league_id: string;
  league_name: string;
  picks: DraftPick[];
}

// Associated trades interface
interface AssociatedTrades {
  originTrade: TradeTreeTrade;
  associatedTrades: TradeTreeTrade[];
}

// Net trade results interfaces
interface NetTradeItem {
  type: 'player' | 'pick';
  player?: TradeTreePlayer;
  pick?: TradeTreePick;
  counterparty: string; // Team that sent/received the item
  tradeDate: string;
  tradeId: string;
}

interface NetTradeResults {
  tradedAway: NetTradeItem[];
  tradedFor: NetTradeItem[];
}

export const TradeTree: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [players, setPlayers] = useState<Record<string, Player>>({});
  const [leagues, setLeagues] = useState<League[]>([]);
  const [allTrades, setAllTrades] = useState<TradeTreeTrade[]>([]);
  const [allDraftPicks, setAllDraftPicks] = useState<DraftData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedTrade, setSelectedTrade] = useState<string>('');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [filteredTrades, setFilteredTrades] = useState<TradeTreeTrade[]>([]);
  const [showDraftPicks, setShowDraftPicks] = useState<boolean>(false);
  const [associatedTrades, setAssociatedTrades] = useState<AssociatedTrades | null>(null);
  const [netTradeResults, setNetTradeResults] = useState<NetTradeResults | null>(null);
  
  // Available options for dropdowns
  const [availableTrades, setAvailableTrades] = useState<{id: string, display: string}[]>([]);
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);

  // Fetch all players from Sleeper API
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        // Check if players data is in sessionStorage and still valid
        const cachedTimestamp = sessionStorage.getItem('sleeper_players_timestamp');
        const currentTime = new Date().getTime();
        
        // Cache valid for 24 hours (86400000 ms)
        const cacheValid = cachedTimestamp && 
          (currentTime - parseInt(cachedTimestamp, 10)) < 86400000;
        
        if (cacheValid) {
          console.log('Using cached players data');
          
          // Load players data in chunks to avoid quota issues
          let allCachedPlayers: Record<string, Player> = {};
          let chunkIndex = 0;
          let chunkData: string | null;
          
          while ((chunkData = sessionStorage.getItem(`sleeper_players_chunk_${chunkIndex}`))) {
            const chunkPlayers = JSON.parse(chunkData);
            allCachedPlayers = { ...allCachedPlayers, ...chunkPlayers };
            chunkIndex++;
          }
          
          if (Object.keys(allCachedPlayers).length > 0) {
            setPlayers(allCachedPlayers);
            return;
          }
        }
        
        // If cache is invalid or doesn't exist, fetch from API
        console.log('Fetching players from API');
        const response = await fetch('https://api.sleeper.app/v1/players/nfl');
        if (!response.ok) {
          throw new Error('Failed to fetch players');
        }
        const fullData = await response.json();
        
        // Process the data to only keep essential fields
        const essentialPlayerData: Record<string, Player> = {};
        Object.entries(fullData).forEach(([playerId, playerData]: [string, any]) => {
          essentialPlayerData[playerId] = {
            player_id: playerId,
            first_name: playerData.first_name || '',
            last_name: playerData.last_name || '',
            position: playerData.position || '',
            team: playerData.team || ''
          };
        });
        
        // Save to sessionStorage in chunks to avoid quota issues
        // Each chunk is about 500KB (rough estimate)
        const playerIds = Object.keys(essentialPlayerData);
        const chunkSize = 1000; // Adjust based on testing
        
        // Clear existing chunks
        let existingChunkIndex = 0;
        while (sessionStorage.getItem(`sleeper_players_chunk_${existingChunkIndex}`)) {
          sessionStorage.removeItem(`sleeper_players_chunk_${existingChunkIndex}`);
          existingChunkIndex++;
        }
        
        // Store new chunks
        for (let i = 0; i < playerIds.length; i += chunkSize) {
          const chunk: Record<string, Player> = {};
          const chunkIds = playerIds.slice(i, i + chunkSize);
          
          chunkIds.forEach(id => {
            chunk[id] = essentialPlayerData[id];
          });
          
          try {
            sessionStorage.setItem(
              `sleeper_players_chunk_${Math.floor(i / chunkSize)}`, 
              JSON.stringify(chunk)
            );
          } catch (e) {
            console.warn('Storage quota exceeded for chunk, proceeding without caching this chunk', e);
            // Continue with the players we've already cached
            break;
          }
        }
        
        // Save timestamp
        sessionStorage.setItem('sleeper_players_timestamp', currentTime.toString());
        
        setPlayers(essentialPlayerData);
      } catch (err) {
        setError('Failed to load players. Please try again later.');
        console.error('Error fetching players:', err);
      }
    };

    fetchPlayers();
  }, []);
  // Fetch draft order for a specific league
  const fetchDraftOrder = async (leagueId: string, season: string) => {
    try {
      // First, get the league data to find the draft_id
      const leagueResponse = await fetch(`https://api.sleeper.app/v1/league/${leagueId}`);
      if (!leagueResponse.ok) {
        console.warn(`Failed to fetch league data for draft info: ${leagueId}`);
        return null;
      }
      
      const leagueData = await leagueResponse.json();
      if (!leagueData || !leagueData.draft_id) {
        console.warn(`No draft_id found in league data for: ${leagueId}`);
        return null;
      }
      
      const draftId = leagueData.draft_id;
      console.log(`Found draft_id: ${draftId} for league: ${leagueId}`);
      
      // Get the draft data which contains slot_to_roster_id mapping
      const draftResponse = await fetch(`https://api.sleeper.app/v1/draft/${draftId}`);
      if (!draftResponse.ok) {
        console.warn(`Failed to fetch draft data for draft_id: ${draftId}`);
        return null;
      }
      
      const draftData = await draftResponse.json();
      if (!draftData || !draftData.slot_to_roster_id) {
        console.warn(`No slot_to_roster_id found in draft data for: ${draftId}`);
        return null;
      }
      
      // Get the slot_to_roster_id mapping
      const slotToRosterId = draftData.slot_to_roster_id || {};
      console.log('slot_to_roster_id mapping:', slotToRosterId);
      
      // Create draft pick map
      const draftPickMap: Record<string, Array<{round: number, pick: number}>> = {};
      
      // Convert slot_to_roster_id mapping to roster_id -> draft position
      const rosterToSlot: Record<number, number> = {};
      
      // Invert the mapping: slot -> roster_id becomes roster_id -> slot
      Object.entries(slotToRosterId).forEach(([slotStr, rosterId]) => {
        const slot = parseInt(slotStr, 10);
        if (!isNaN(slot)) {
          rosterToSlot[rosterId as number] = slot; 
        }
      });
      
      // Now for each roster ID, we know their draft slot (1-based)
      Object.entries(rosterToSlot).forEach(([rosterIdStr, draftSlot]) => {
        const rosterId = parseInt(rosterIdStr, 10);
        if (!isNaN(rosterId)) {
          const key = `${season}_${rosterId}`;
          
          draftPickMap[key] = [{
            round: 1,
            pick: draftSlot
          }];
          
          console.log(`Added draft pick for roster ${rosterId} at slot ${draftSlot}`);
        }
      });
      
      return draftPickMap;
    } catch (err) {
      console.error('Error fetching draft order:', err);
      return null;
    }
  };
  // Fetch all draft picks for a specific league
  const fetchAllDraftPicks = async (
    leagueId: string, 
    season: string, 
    leagueName: string,
    rosterToOwnerMap: Record<number, string>,
    userIdToNameMap: Record<string, string>
  ): Promise<DraftData | null> => {
    try {
      // Get all drafts for the league
      const draftsResponse = await fetch(`https://api.sleeper.app/v1/league/${leagueId}/drafts`);
      if (!draftsResponse.ok) {
        console.warn(`Failed to fetch drafts for league: ${leagueId}`);
        return null;
      }
      
      const draftsData = await draftsResponse.json();
      if (!Array.isArray(draftsData) || draftsData.length === 0) {
        console.warn(`No drafts found for league: ${leagueId}`);
        return null;
      }
      
      // Find drafts with 2-3 rounds
      const validDrafts = draftsData.filter((draft: any) => 
        draft.settings && draft.settings.rounds >= 2 && draft.settings.rounds <= 3
      );
      
      if (validDrafts.length === 0) {
        console.log(`No drafts with 2-3 rounds found for league ${leagueId}`);
        return null;
      }
      
      // Use the first valid draft (typically there's only one per league)
      const selectedDraft = validDrafts[0];
      const draftId = selectedDraft.draft_id;
      
      console.log(`Found valid draft ${draftId} with ${selectedDraft.settings.rounds} rounds for league ${leagueId}`);
      
      // Get all draft picks for the selected draft
      const picksResponse = await fetch(`https://api.sleeper.app/v1/draft/${draftId}/picks`);
      if (!picksResponse.ok) {
        console.warn(`Failed to fetch draft picks for draft_id: ${draftId}`);
        return null;
      }
      
      const picksData = await picksResponse.json();
      if (!Array.isArray(picksData)) {
        console.warn(`Invalid draft picks data for draft_id: ${draftId}`);
        return null;
      }

      console.log(`Including draft ${draftId} - has ${selectedDraft.settings.rounds} rounds`);

      // Process picks and add player names and team names
      const processedPicks: DraftPick[] = picksData.map((pick: any) => {
        const player = players[pick.player_id];
        const playerName = player 
          ? `${player.first_name} ${player.last_name}` 
          : `Unknown Player (${pick.player_id})`;
        
        const userId = rosterToOwnerMap[pick.roster_id];
        const teamName = userIdToNameMap[userId] || `Unknown Team (${pick.roster_id})`;
        
        return {
          pick_no: pick.pick_no,
          player_id: pick.player_id,
          picked_by: pick.picked_by,
          roster_id: pick.roster_id,
          round: pick.round,
          draft_slot: pick.draft_slot,
          draft_id: draftId,
          season: season,
          league_id: leagueId,
          player_name: playerName,
          team_name: teamName
        };
      });

      return {
        draft_id: draftId,
        season: season,
        league_id: leagueId,
        league_name: leagueName,
        picks: processedPicks
      };
    } catch (err) {
      console.error('Error fetching draft picks:', err);
      return null;
    }
  };

  // Fetch league information and transactions
  useEffect(() => {
    if (!leagueId) {
      setError('No league ID provided');
      setLoading(false);
      return;
    }    const fetchLeaguesAndTrades = async () => {
      try {
        setLoading(true);
        let currentLeagueId = leagueId;
        const leagueChain: League[] = [];
        const allTradesData: TradeTreeTrade[] = [];
        const allDraftPicksData: DraftData[] = [];
        const draftPickMaps: Record<string, Record<string, Array<{round: number, pick: number}>>> = {};

        // Loop through league chain until previous_league_id is null or 0
        while (currentLeagueId && currentLeagueId !== '0') {
          // Get league info
          const leagueResponse = await fetch(`https://api.sleeper.app/v1/league/${currentLeagueId}`);
          if (!leagueResponse.ok) {
            throw new Error(`Failed to fetch league with ID: ${currentLeagueId}`);
          }
          const leagueData: League = await leagueResponse.json();
          leagueChain.push(leagueData);

          // Get roster mapping (to match roster_id with user_id)
          const rosterResponse = await fetch(`https://api.sleeper.app/v1/league/${currentLeagueId}/rosters`);
          if (!rosterResponse.ok) {
            throw new Error(`Failed to fetch rosters for league: ${currentLeagueId}`);
          }
          const rostersData = await rosterResponse.json();
          
          const rosterToOwnerMap: Record<number, string> = {};
          rostersData.forEach((roster: any) => {
            rosterToOwnerMap[roster.roster_id] = roster.owner_id;
          });

          // Get users mapping (to match user_id with display_name)
          const usersResponse = await fetch(`https://api.sleeper.app/v1/league/${currentLeagueId}/users`);
          if (!usersResponse.ok) {
            throw new Error(`Failed to fetch users for league: ${currentLeagueId}`);
          }
          const usersData = await usersResponse.json();
          
          const userIdToNameMap: Record<string, string> = {};
          usersData.forEach((user: any) => {
            userIdToNameMap[user.user_id] = user.display_name;
          });

          // Fetch all draft picks for this league
          console.log(`Fetching draft picks for league ${currentLeagueId}`);
          const draftPicksData = await fetchAllDraftPicks(
            currentLeagueId, 
            leagueData.season, 
            leagueData.name,
            rosterToOwnerMap,
            userIdToNameMap
          );
          if (draftPicksData) {
            allDraftPicksData.push(draftPicksData);
            console.log(`Added ${draftPicksData.picks.length} draft picks from ${leagueData.season} season`);
          }

          // Get all transactions from all pages (1-18)
          let allTransactionsForLeague: Transaction[] = [];
          console.log(`Fetching transactions for league ${currentLeagueId}`);
          
          // Process up to 18 pages of transactions
          for (let page = 1; page <= 24; page++) {
            try {
              const transactionsResponse = await fetch(`https://api.sleeper.app/v1/league/${currentLeagueId}/transactions/${page}`);
              if (!transactionsResponse.ok) {
                // 404 means we've reached the end of available pages
                if (transactionsResponse.status === 404) {
                  console.log(`No more transaction pages available after page ${page-1}`);
                  break;
                }
                throw new Error(`Failed to fetch transactions for league: ${currentLeagueId}, page: ${page}`);
              }
              
              const pageTransactions: Transaction[] = await transactionsResponse.json();
              console.log(`Retrieved ${pageTransactions.length} transactions from page ${page}`);
              
              // If empty page, we've reached the end
              if (pageTransactions.length === 0) {
                console.log(`No more transactions on page ${page}`);
                break;
              }
              
              allTransactionsForLeague.push(...pageTransactions);
            } catch (err) {
              console.error(`Error fetching transactions page ${page}:`, err);
              // Continue with the pages we've already fetched
              break;
            }
          }
          
          // Filter for trades
          const tradeTransactions = allTransactionsForLeague.filter(transaction => transaction.type === 'trade');
          console.log(`Found ${tradeTransactions.length} trades in league ${currentLeagueId}`);
          
          // Always fetch draft data for the league
          console.log(`Fetching draft data for league ${currentLeagueId}`);
          const draftPickMap = await fetchDraftOrder(currentLeagueId, leagueData.season);
          if (draftPickMap) {
            draftPickMaps[currentLeagueId] = draftPickMap;
            console.log(`Draft data fetched successfully, keys: ${Object.keys(draftPickMap).length}`);
          } else {
            console.warn(`No draft data available for league ${currentLeagueId}`);
          }

          // Process trade transactions into TradeTree format
          const tradesInLeague = tradeTransactions.map(trade => {
            const tradeDate = new Date(trade.created);
            const formattedDate = `${tradeDate.getMonth() + 1}/${tradeDate.getDate()}/${tradeDate.getFullYear()}`;
            
            const traders: TradeTreeTrader[] = trade.roster_ids.map(rosterId => {
              const userId = rosterToOwnerMap[rosterId];
              const teamName = userIdToNameMap[userId] || `Unknown Team (${rosterId})`;
              
              // Get players this team traded away
              const tradedAwayPlayers: TradeTreePlayer[] = [];
              if (trade.drops) {
                Object.entries(trade.drops).forEach(([playerId, droppedRosterId]) => {
                  if (droppedRosterId === rosterId) {
                    const player = players[playerId];
                    const playerName = player 
                      ? `${player.first_name} ${player.last_name}`
                      : `Unknown Player (${playerId})`;
                    tradedAwayPlayers.push({ id: playerId, name: playerName });
                  }
                });
              }
              
              // Get players this team received
              const receivedPlayers: TradeTreePlayer[] = [];
              if (trade.adds) {
                Object.entries(trade.adds).forEach(([playerId, addedRosterId]) => {
                  if (addedRosterId === rosterId) {
                    const player = players[playerId];
                    const playerName = player 
                      ? `${player.first_name} ${player.last_name}`
                      : `Unknown Player (${playerId})`;
                    receivedPlayers.push({ id: playerId, name: playerName });
                  }
                });
              }
              
              // Process draft picks
              const tradedAwayPicks: TradeTreePick[] = [];
              const receivedPicks: TradeTreePick[] = [];
              
              if (trade.draft_picks) {
                const draftPickMap = draftPickMaps[currentLeagueId];
                
                trade.draft_picks.forEach(pick => {
                  const pickInfo: TradeTreePick = {
                    year: pick.season,
                    round: pick.round
                  };
                  
                  // Try to get pick number from draft data
                  if (draftPickMap) {
                    const pickKey = `${pick.season}_${pick.roster_id}`;
                    const picks = draftPickMap[pickKey];
                    if (picks && Array.isArray(picks) && picks.length > 0) {
                      pickInfo.pickNumber = picks[0].pick;
                    } else {
                      // Check other leagues in the chain
                      Object.values(draftPickMaps).forEach(leagueDraftMap => {
                        const picks = leagueDraftMap[pickKey];
                        if (picks && Array.isArray(picks) && picks.length > 0) {
                          pickInfo.pickNumber = picks[0].pick;
                        }
                      });
                    }
                  }
                    // Determine if this team traded away or received the pick
                  if (pick.owner_id === rosterId) {
                    // This team received the pick
                    receivedPicks.push(pickInfo);
                  } else if (pick.previous_owner_id === rosterId) {
                    // This team traded away the pick
                    tradedAwayPicks.push(pickInfo);
                  }
                });
              }
              
              return {
                team: teamName,
                tradedAwayPicks,
                tradedAwayPlayers,
                receivedPicks,
                receivedPlayers
              };
            });

            return {
              id: trade.transaction_id,
              date: formattedDate,
              traders
            };
          });

          allTradesData.push(...tradesInLeague);
          
          // Move to previous league
          currentLeagueId = leagueData.previous_league_id;
        }        setLeagues(leagueChain);
        setAllTrades(allTradesData);
        setAllDraftPicks(allDraftPicksData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load league data. Please try again later.');
        console.error('Error fetching league data:', err);
        setLoading(false);
      }
    };

    fetchLeaguesAndTrades();
  }, [leagueId, players]);
  // Update available options when trades change
  useEffect(() => {
    if (allTrades.length > 0) {
      // Get available trades
      const tradeOptions = allTrades.map((trade: TradeTreeTrade) => ({
        id: trade.id,
        display: `${trade.date} - ${trade.traders.map((t: TradeTreeTrader) => t.team).join(' vs ')}`
      }));
      setAvailableTrades(tradeOptions);
      
      // Get available teams
      const teamSet = new Set<string>();
      allTrades.forEach((trade: TradeTreeTrade) => {
        trade.traders.forEach((trader: TradeTreeTrader) => {
          teamSet.add(trader.team);
        });
      });
      setAvailableTeams(Array.from(teamSet).sort());
    }
  }, [allTrades]);
  // Update filtered trades when selections change
  useEffect(() => {
    let filtered = allTrades;
    
    if (selectedTrade) {
      filtered = filtered.filter((trade: TradeTreeTrade) => trade.id === selectedTrade);
    }
    
    if (selectedTeam) {
      filtered = filtered.filter((trade: TradeTreeTrade) => 
        trade.traders.some((trader: TradeTreeTrader) => trader.team === selectedTeam)
      );
    }
    
    setFilteredTrades(filtered);
  }, [selectedTrade, selectedTeam, allTrades]);
  // Get filtered dropdown options based on current selections
  const getFilteredTradeOptions = () => {
    if (selectedTeam) {
      return availableTrades.filter((trade: {id: string, display: string}) => 
        allTrades.find((t: TradeTreeTrade) => t.id === trade.id)?.traders.some((trader: TradeTreeTrader) => trader.team === selectedTeam)
      );
    }
    return availableTrades;
  };

  const getFilteredTeamOptions = () => {
    if (selectedTrade) {
      const trade = allTrades.find((t: TradeTreeTrade) => t.id === selectedTrade);
      return trade ? trade.traders.map((trader: TradeTreeTrader) => trader.team) : [];
    }
    return availableTeams;
  };
  const handleTradeChange = (tradeId: string) => {
    setSelectedTrade(tradeId);
    // Clear team selection if it's not valid for the selected trade
    if (tradeId && selectedTeam) {
      const trade = allTrades.find((t: TradeTreeTrade) => t.id === tradeId);
      if (trade && !trade.traders.some((trader: TradeTreeTrader) => trader.team === selectedTeam)) {
        setSelectedTeam('');
      }
    }
  };
  const handleTeamChange = (team: string) => {
    setSelectedTeam(team);
    // Clear trade selection if it doesn't involve the selected team
    if (team && selectedTrade) {
      const trade = allTrades.find((t: TradeTreeTrade) => t.id === selectedTrade);
      if (trade && !trade.traders.some((trader: TradeTreeTrader) => trader.team === team)) {
        setSelectedTrade('');
      }
    }
  };
  // Function to find associated trades based on selected trade and team
  const findAssociatedTrades = (originTrade: TradeTreeTrade, originTeam: string): TradeTreeTrade[] => {
    const associatedTradesList: TradeTreeTrade[] = [];
    const processedTradeIds = new Set<string>([originTrade.id]);
    
    // Helper function to convert date string to Date object for comparison
    const parseTradeDate = (dateStr: string): Date => {
      const [month, day, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };
    
    const originDate = parseTradeDate(originTrade.date);
    
    // Get all trades that happened after the origin trade and include the origin team
    const futureTrades = allTrades.filter(trade => {
      const tradeDate = parseTradeDate(trade.date);
      const includesOriginTeam = trade.traders.some(trader => trader.team === originTeam);
      return tradeDate > originDate && !processedTradeIds.has(trade.id) && includesOriginTeam;
    }).sort((a, b) => parseTradeDate(a.date).getTime() - parseTradeDate(b.date).getTime());

    // Find the origin team's trader info
    const originTrader = originTrade.traders.find(trader => trader.team === originTeam);
    if (!originTrader) return associatedTradesList;

    // Helper function to find drafted player for a pick
    const findDraftedPlayerForPick = (pick: TradeTreePick): TradeTreePlayer | null => {
      for (const draftData of allDraftPicks) {
        for (const draftPick of draftData.picks) {
          // Match by year and round, and if we have pick number, match that too
          if (draftData.season === pick.year && draftPick.round === pick.round) {
            if (pick.pickNumber && draftPick.pick_no === pick.pickNumber) {
              // Exact match with pick number
              return {
                id: draftPick.player_id,
                name: draftPick.player_name || `Unknown Player (${draftPick.player_id})`
              };
            } else if (!pick.pickNumber) {
              // If we don't have a specific pick number, just match year and round
              return {
                id: draftPick.player_id,
                name: draftPick.player_name || `Unknown Player (${draftPick.player_id})`
              };
            }
          }
        }
      }
      return null;
    };

    // Collect all items that the origin team received (these are what we're tracking)
    const trackedPlayers = new Set<string>();
    const trackedPicks = new Set<string>();

    originTrader.receivedPlayers.forEach(player => trackedPlayers.add(player.id));
    originTrader.receivedPicks.forEach(pick => {
      const pickKey = `${pick.year}_${pick.round}${pick.pickNumber ? `_${pick.pickNumber}` : ''}`;
      trackedPicks.add(pickKey);
      
      // Also track the player drafted with this pick (if we can find them)
      const draftedPlayer = findDraftedPlayerForPick(pick);
      if (draftedPlayer) {
        console.log(`Tracking drafted player ${draftedPlayer.name} from pick ${pick.year}.${pick.round}${pick.pickNumber ? `.${pick.pickNumber}` : ''}`);
        trackedPlayers.add(draftedPlayer.id);
      }
    });

    // Recursive function to find trades involving tracked items
    const findMatchingTrades = (currentTrackedPlayers: Set<string>, currentTrackedPicks: Set<string>) => {
      const newTrackedPlayers = new Set(currentTrackedPlayers);
      const newTrackedPicks = new Set(currentTrackedPicks);
      let foundNewTrade = false;
      
      for (const futureTrade of futureTrades) {
        if (processedTradeIds.has(futureTrade.id)) continue;

        let tradeMatches = false;
        let matchingTeam: string | null = null;

        // Find the origin team's trader in this future trade
        const originTeamTrader = futureTrade.traders.find(trader => trader.team === originTeam);
        if (!originTeamTrader) continue; // Skip if origin team is not in this trade

        // Check if the origin team is trading away any tracked players
        for (const tradedPlayer of originTeamTrader.tradedAwayPlayers) {
          if (currentTrackedPlayers.has(tradedPlayer.id)) {
            tradeMatches = true;
            matchingTeam = originTeam;
            console.log(`Found associated trade: ${originTeam} traded away tracked player ${tradedPlayer.name} on ${futureTrade.date}`);
            break;
          }
        }

        // Check if the origin team is trading away any tracked picks
        if (!tradeMatches) {
          for (const tradedPick of originTeamTrader.tradedAwayPicks) {
            const pickKey = `${tradedPick.year}_${tradedPick.round}${tradedPick.pickNumber ? `_${tradedPick.pickNumber}` : ''}`;
            if (currentTrackedPicks.has(pickKey)) {
              tradeMatches = true;
              matchingTeam = originTeam;
              console.log(`Found associated trade: ${originTeam} traded away tracked pick ${tradedPick.year}.${tradedPick.round}${tradedPick.pickNumber ? `.${tradedPick.pickNumber}` : ''} on ${futureTrade.date}`);
              break;
            }
          }
        }

        // If we found a matching trade, add it and track new items received by origin team
        if (tradeMatches && matchingTeam) {
          associatedTradesList.push(futureTrade);
          processedTradeIds.add(futureTrade.id);
          foundNewTrade = true;

          // Add the origin team's received items to tracking for the next iteration
          originTeamTrader.receivedPlayers.forEach(player => {
            newTrackedPlayers.add(player.id);
            console.log(`Now tracking received player: ${player.name}`);
          });
          
          originTeamTrader.receivedPicks.forEach(pick => {
            const pickKey = `${pick.year}_${pick.round}${pick.pickNumber ? `_${pick.pickNumber}` : ''}`;
            newTrackedPicks.add(pickKey);
            console.log(`Now tracking received pick: ${pick.year}.${pick.round}${pick.pickNumber ? `.${pick.pickNumber}` : ''}`);
            
            // Also track the player drafted with this newly received pick
            const draftedPlayer = findDraftedPlayerForPick(pick);
            if (draftedPlayer) {
              newTrackedPlayers.add(draftedPlayer.id);
              console.log(`Now tracking drafted player ${draftedPlayer.name} from newly received pick`);
            }
          });
        }
      }

      // If we found new trades, recursively search for more
      if (foundNewTrade) {
        findMatchingTrades(newTrackedPlayers, newTrackedPicks);
      }
    };
    
    findMatchingTrades(trackedPlayers, trackedPicks);
    return associatedTradesList;
  };

  // Helper function to create a unique key for an item (player or pick)
  const createItemKey = (item: NetTradeItem): string => {
    if (item.type === 'player') {
      return `player_${item.player?.id || 'unknown'}`;
    } else {
      const pick = item.pick;
      return `pick_${pick?.year}_${pick?.round}_${pick?.pickNumber || 'unknown'}`;
    }
  };

  // Function to calculate net trade results for the selected team from origin and associated trades only
  const calculateNetTradeResults = (teamName: string, originTrade: TradeTreeTrade, associatedTradesList: TradeTreeTrade[]): NetTradeResults => {
    const tradedAway: NetTradeItem[] = [];
    const tradedFor: NetTradeItem[] = [];

    // Build optimized lookup map for draft picks (built once, reused many times)
    const draftPickLookup = new Map<string, TradeTreePlayer>();
    
    allDraftPicks.forEach(draftData => {
      draftData.picks.forEach(draftPick => {
        if (draftPick.player_name) {
          // Create keys for both specific pick number and general round lookups
          if (draftPick.pick_no) {
            // Specific pick lookup: "2023_1_3" (year_round_pickNumber)
            const specificKey = `${draftData.season}_${draftPick.round}_${draftPick.pick_no}`;
            draftPickLookup.set(specificKey, {
              id: draftPick.player_id,
              name: draftPick.player_name
            });
          }
          
          // General round lookup: "2023_1" (year_round) - for picks without specific numbers
          const generalKey = `${draftData.season}_${draftPick.round}`;
          if (!draftPickLookup.has(generalKey)) {
            draftPickLookup.set(generalKey, {
              id: draftPick.player_id,
              name: draftPick.player_name
            });
          }
        }
      });
    });

    // Simplified helper function using the lookup map
    const findDraftedPlayer = (pick: TradeTreePick): TradeTreePlayer | undefined => {
      // Try specific pick lookup first (most accurate)
      if (pick.pickNumber) {
        const specificKey = `${pick.year}_${pick.round}_${pick.pickNumber}`;
        const player = draftPickLookup.get(specificKey);
        if (player) {
          return player;
        }
      }
      
      // Fall back to general round lookup
      const generalKey = `${pick.year}_${pick.round}`;
      return draftPickLookup.get(generalKey);
    };    // Process trades - only origin trade and associated trades
    const tradesToProcess = [originTrade, ...associatedTradesList];
    
    tradesToProcess.forEach(trade => {
      const teamTrader = trade.traders.find(trader => trader.team === teamName);
      if (!teamTrader) return;



      // Find the other team(s) in the trade
      const otherTraders = trade.traders.filter(trader => trader.team !== teamName);

      // Process players traded away
      teamTrader.tradedAwayPlayers.forEach(player => {
        // Find which team received this player
        let receivingTeam = 'Unknown';
        for (const otherTrader of otherTraders) {
          if (otherTrader.receivedPlayers.some(p => p.id === player.id)) {
            receivingTeam = otherTrader.team;
            break;
          }
        }

        tradedAway.push({
          type: 'player',
          player,
          counterparty: receivingTeam,
          tradeDate: trade.date,
          tradeId: trade.id
        });
      });      // Process picks traded away
      teamTrader.tradedAwayPicks.forEach(pick => {
        console.log(`Processing traded away pick:`, pick);
        
        // Find which team received this pick
        let receivingTeam = 'Unknown';
        for (const otherTrader of otherTraders) {
          if (otherTrader.receivedPicks.some(p => 
            p.year === pick.year && p.round === pick.round && p.pickNumber === pick.pickNumber)) {
            receivingTeam = otherTrader.team;
            break;
          }
        }

        // Enhance pick with drafted player info
        const enhancedPick = { ...pick };
        const draftedPlayer = findDraftedPlayer(pick);
        if (draftedPlayer) {
          enhancedPick.playerDrafted = draftedPlayer;
        }

        tradedAway.push({
          type: 'pick',
          pick: enhancedPick,
          counterparty: receivingTeam,
          tradeDate: trade.date,
          tradeId: trade.id
        });
        
        console.log(`Added traded away pick to results:`, enhancedPick);
      });

      // Process players traded for
      teamTrader.receivedPlayers.forEach(player => {
        // Find which team sent this player
        let sendingTeam = 'Unknown';
        for (const otherTrader of otherTraders) {
          if (otherTrader.tradedAwayPlayers.some(p => p.id === player.id)) {
            sendingTeam = otherTrader.team;
            break;
          }
        }

        tradedFor.push({
          type: 'player',
          player,
          counterparty: sendingTeam,
          tradeDate: trade.date,
          tradeId: trade.id
        });
      });      // Process picks traded for
      teamTrader.receivedPicks.forEach(pick => {
        console.log(`Processing received pick:`, pick);
        
        // Find which team sent this pick
        let sendingTeam = 'Unknown';
        for (const otherTrader of otherTraders) {
          if (otherTrader.tradedAwayPicks.some(p => 
            p.year === pick.year && p.round === pick.round && p.pickNumber === pick.pickNumber)) {
            sendingTeam = otherTrader.team;
            break;
          }
        }

        // Enhance pick with drafted player info
        const enhancedPick = { ...pick };
        const draftedPlayer = findDraftedPlayer(pick);
        if (draftedPlayer) {
          enhancedPick.playerDrafted = draftedPlayer;
        }

        tradedFor.push({
          type: 'pick',
          pick: enhancedPick,
          counterparty: sendingTeam,
          tradeDate: trade.date,
          tradeId: trade.id
        });
        
        console.log(`Added received pick to results:`, enhancedPick);
      });
    });

    // Sort by trade date (most recent first)
    const sortByDate = (a: NetTradeItem, b: NetTradeItem) => {
      const parseDate = (dateStr: string) => {
        const [month, day, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day);
      };
      return parseDate(b.tradeDate).getTime() - parseDate(a.tradeDate).getTime();
    };

    tradedAway.sort(sortByDate);
    tradedFor.sort(sortByDate);

    return { tradedAway, tradedFor };
  };

  // Update associated trades when selection changes
  useEffect(() => {
    if (selectedTrade && selectedTeam && allTrades.length > 0) {
      const originTrade = allTrades.find(trade => trade.id === selectedTrade);
      if (originTrade) {
        const associated = findAssociatedTrades(originTrade, selectedTeam);
        setAssociatedTrades({
          originTrade,
          associatedTrades: associated
        });
      }
    } else {
      setAssociatedTrades(null);
    }
  }, [selectedTrade, selectedTeam, allTrades]);
  // Update net trade results when team is selected and we have associated trades
  useEffect(() => {
    if (selectedTeam && associatedTrades && allTrades.length > 0) {
      const netResults = calculateNetTradeResults(selectedTeam, associatedTrades.originTrade, associatedTrades.associatedTrades);
      setNetTradeResults(netResults);
    } else {
      setNetTradeResults(null);
    }
  }, [selectedTeam, associatedTrades, allTrades, allDraftPicks]);

  if (loading) {
    return <div className="loading">Loading trade data...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="trade-tree-container">
      <h1>Trade Tree</h1>
        {leagues.length > 0 && (
        <div className="league-info">
          <h2>{leagues[0].name} - League Chain</h2>
          <div className="league-chain">
            {leagues.map((league: League, index: number) => (
              <div key={league.league_id} className="league-chain-item">
                {index > 0 && <span className="chain-arrow">→</span>}
                <span>{league.season} Season</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="filters">        <div className="filter-group">
          <label htmlFor="trade-select">Select Trade:</label>
          <select 
            id="trade-select"
            value={selectedTrade} 
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTradeChange(e.target.value)}
          >
            <option value="">All Trades</option>
            {getFilteredTradeOptions().map((trade: {id: string, display: string}) => (
              <option key={trade.id} value={trade.id}>
                {trade.display}
              </option>
            ))}
          </select>
        </div>        <div className="filter-group">
          <label htmlFor="team-select">Select Team:</label>
          <select 
            id="team-select"
            value={selectedTeam} 
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTeamChange(e.target.value)}
          >
            <option value="">All Teams</option>
            {getFilteredTeamOptions().map((team: string) => (
              <option key={team} value={team}>
                {team}
              </option>
            ))}
          </select>
        </div>      </div>

      <div className="draft-picks-section">
        <button 
          className="toggle-draft-picks-btn"
          onClick={() => setShowDraftPicks(!showDraftPicks)}
        >
          {showDraftPicks ? 'Hide' : 'Show'} All Draft Picks ({allDraftPicks.reduce((total, draft) => total + draft.picks.length, 0)} total)
        </button>
          {showDraftPicks && allDraftPicks.length > 0 && (
          <div className="draft-picks-results">
            <h3>All Draft Picks from League Chain (2-3 Round Drafts Only)</h3>
            <pre className="json-output">
              {JSON.stringify(allDraftPicks, null, 2)}
            </pre>          </div>
        )}      </div>      {netTradeResults && selectedTeam && (() => {
        // Create sets of item keys for duplicate detection
        const tradedAwayKeys = new Set(netTradeResults.tradedAway.map(createItemKey));
        const tradedForKeys = new Set(netTradeResults.tradedFor.map(createItemKey));
        
        return (
          <div className="net-trade-results-section">
            <h3>Net Trade Results for {selectedTeam}</h3>
            
            <div className="trade-tables-container">
              <div className="trade-table-section">
                <h4>Traded Away ({netTradeResults.tradedAway.length} items)</h4>
                <div className="trade-table-wrapper">
                  <table className="trade-table">
                    <thead>
                      <tr>
                        <th>Traded Away</th>
                        <th>To Team</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {netTradeResults.tradedAway.length > 0 ? (
                        netTradeResults.tradedAway.map((item, index) => {
                          const itemKey = createItemKey(item);
                          const isDuplicate = tradedForKeys.has(itemKey);
                          
                          return (
                            <tr key={`away-${index}`} className={isDuplicate ? 'duplicate-item' : ''}>
                              <td>
                                {item.type === 'player' ? (
                                  item.player?.name || 'Unknown Player'
                                ) : (
                                  <>
                                    {item.pick?.year}.{item.pick?.round}{item.pick?.pickNumber ? `.${item.pick.pickNumber}` : ''}
                                    {item.pick?.playerDrafted && (
                                      <span className="drafted-player"> ({item.pick.playerDrafted.name})</span>
                                    )}
                                  </>
                                )}
                              </td>
                              <td>{item.counterparty}</td>
                              <td>{item.tradeDate}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={3} className="no-data">No items traded away</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="trade-table-section">
                <h4>Traded For ({netTradeResults.tradedFor.length} items)</h4>
                <div className="trade-table-wrapper">
                  <table className="trade-table">
                    <thead>
                      <tr>
                        <th>Traded For</th>
                        <th>From Team</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {netTradeResults.tradedFor.length > 0 ? (
                        netTradeResults.tradedFor.map((item, index) => {
                          const itemKey = createItemKey(item);
                          const isDuplicate = tradedAwayKeys.has(itemKey);
                          
                          return (
                            <tr key={`for-${index}`} className={isDuplicate ? 'duplicate-item' : ''}>
                              <td>
                                {item.type === 'player' ? (
                                  item.player?.name || 'Unknown Player'
                                ) : (
                                  <>
                                    {item.pick?.year}.{item.pick?.round}{item.pick?.pickNumber ? `.${item.pick.pickNumber}` : ''}
                                    {item.pick?.playerDrafted && (
                                      <span className="drafted-player"> ({item.pick.playerDrafted.name})</span>
                                    )}
                                  </>
                                )}
                              </td>
                              <td>{item.counterparty}</td>
                              <td>{item.tradeDate}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={3} className="no-data">No items traded for</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {associatedTrades && (
        <div className="associated-trades-section">
          <h3>Associated Trades Chain</h3>
          <p>Starting from: <strong>{associatedTrades.originTrade.date}</strong> - <strong>{selectedTeam}</strong></p>
          <p>Found <strong>{associatedTrades.associatedTrades.length}</strong> associated trades</p>
          <pre className="json-output">
            {JSON.stringify(associatedTrades, null, 2)}
          </pre>
        </div>
      )}

      {filteredTrades.length > 0 ? (
        <div className="trade-results">
          <h3>Filtered Trades ({filteredTrades.length})</h3>
          <pre className="json-output">
            {JSON.stringify(filteredTrades, null, 2)}
          </pre>
        </div>
      ) : (
        <div className="no-results">
          {allTrades.length > 0 ? 'No trades match the selected filters.' : 'No trades found for this league chain.'}
        </div>
      )}
    </div>
  );
};

export default TradeTree;